// Live Activities bridge — iOS native only.
// Three activity kinds share one plugin / one ActivityAttributes type:
//   "nextPrayer" — countdown to the next prayer (always running)
//   "jumuah"    — countdown to Jumu'ah (Friday only)
//   "tahajjud"  — last-third-of-night window (late evening only)
//
// Web/Android paths are no-ops.

function isNative() {
  return !!window.Capacitor?.isNativePlatform?.();
}

function getPlugin() {
  return window.Capacitor?.Plugins?.SiratActivity;
}

export async function isLiveActivitiesAvailable() {
  if (!isNative()) return false;
  const plugin = getPlugin();
  if (!plugin) return false;
  try {
    const r = await plugin.isAvailable();
    return !!r?.available;
  } catch {
    return false;
  }
}

export async function startActivity({ kind, primaryName, startsAt, endsAt, subtitle, allPrayers, countdownLabel, compactCountdown }) {
  if (!isNative()) return null;
  const plugin = getPlugin();
  if (!plugin) return null;
  try {
    const r = await plugin.start({
      kind,
      primaryName,
      startsAtMs: startsAt instanceof Date ? startsAt.getTime() : Number(startsAt),
      endsAtMs: endsAt instanceof Date ? endsAt.getTime() : Number(endsAt),
      subtitle: subtitle || "",
      countdownLabel: countdownLabel || "",
      compactCountdown: compactCountdown || "",
      allPrayers: (allPrayers || []).map(p => ({
        name: p.name,
        timeMs: p.time instanceof Date ? p.time.getTime() : Number(p.time || 0),
        prayed: !!p.prayed
      }))
    });
    return r?.id || null;
  } catch {
    return null;
  }
}

// Update a running Live Activity in place. Returns true on success, false if no
// matching activity is running. Used by the reconcile loop to push new
// compactCountdown strings (e.g. "32m" → "31m") without tearing down and
// recreating the activity.
export async function updateActivity({ kind, primaryName, startsAt, endsAt, subtitle, allPrayers, countdownLabel, compactCountdown }) {
  if (!isNative()) return false;
  const plugin = getPlugin();
  if (!plugin) return false;
  try {
    const args = { kind };
    if (primaryName !== undefined) args.primaryName = primaryName;
    if (startsAt !== undefined) args.startsAtMs = startsAt instanceof Date ? startsAt.getTime() : Number(startsAt);
    if (endsAt !== undefined) args.endsAtMs = endsAt instanceof Date ? endsAt.getTime() : Number(endsAt);
    if (subtitle !== undefined) args.subtitle = subtitle;
    if (countdownLabel !== undefined) args.countdownLabel = countdownLabel;
    if (compactCountdown !== undefined) args.compactCountdown = compactCountdown;
    if (allPrayers !== undefined) {
      args.allPrayers = (allPrayers || []).map(p => ({
        name: p.name,
        timeMs: p.time instanceof Date ? p.time.getTime() : Number(p.time || 0),
        prayed: !!p.prayed
      }));
    }
    const r = await plugin.update(args);
    return !!r?.updated;
  } catch {
    return false;
  }
}

// Pre-format a minute-resolution countdown string for the Dynamic Island
// compact view. Examples: "now", "1m", "32m", "1h", "8h". Always 2-3 chars so
// the pill stays close to the static idle width.
export function formatCompactCountdown(endsAt, now) {
  if (!endsAt) return "";
  const endMs = endsAt instanceof Date ? endsAt.getTime() : Number(endsAt);
  const nowMs = now ? (now instanceof Date ? now.getTime() : Number(now)) : Date.now();
  const min = Math.floor((endMs - nowMs) / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m`;
  return `${Math.round(min / 60)}h`;
}

export async function endActivity(kind, immediate = true) {
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin) return;
  try { await plugin.end({ kind, immediate }); } catch {}
}

export async function endAllActivities() {
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin) return;
  try { await plugin.endAll(); } catch {}
}

// Wires the iOS-side `pushTokenUpdate` listener once at app startup. Each
// time iOS hands us a Live Activity push token (one per Activity.request),
// POST it to azaan-backend so the server can schedule a `liveactivity` end
// push at endsAt+30min. Without this, when the salaah time passes while the
// phone is locked / app is force-quit, the lock-screen card freezes on the
// muted countdown ring and only refreshes when the user re-opens the app.
//
// Call once from App startup with the BACKEND base URL. Idempotent —
// re-invoking does nothing (the listener is added on the first call only).
let _pushTokenListenerInstalled = false;
export function installLiveActivityPushTokenListener(backendUrl) {
  if (_pushTokenListenerInstalled) return;
  if (!isNative()) return;
  const plugin = getPlugin();
  if (!plugin || typeof plugin.addListener !== "function") return;
  plugin.addListener("pushTokenUpdate", async (event) => {
    try {
      const token = event?.token;
      const kind = event?.kind;
      const endsAtMs = event?.endsAtMs;
      if (!token || !kind || !endsAtMs) return;
      await fetch(`${backendUrl}/api/live-activity/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, kind, endsAtMs })
      });
    } catch {
      // Network errors are non-fatal — JS reconcile re-registers next tick.
    }
  });
  _pushTokenListenerInstalled = true;
}

export async function getActivityDebug() {
  const out = {
    native: isNative(),
    pluginPresent: !!getPlugin(),
    areActivitiesEnabled: false,
    activeCount: 0,
    active: [],
    lastError: "",
    lastStartAttempt: "",
    bridgeError: null
  };
  if (!out.native || !out.pluginPresent) return out;
  try {
    const r = await getPlugin().getDebugInfo();
    out.areActivitiesEnabled = !!r?.areActivitiesEnabled;
    out.frequentPushesEnabled = !!r?.frequentPushesEnabled;
    out.activeCount = r?.activeCount ?? 0;
    out.active = r?.active ?? [];
    out.lastError = r?.lastError ?? "";
    out.lastStartAttempt = r?.lastStartAttempt ?? "";
  } catch (e) {
    out.bridgeError = String(e);
  }
  return out;
}

// ── Helpers for parsing Aladhan's HH:mm strings into today's Date objects ──

export function todayAt(hhmm) {
  if (!hhmm) return null;
  const cleaned = String(hhmm).replace(/\s*\(.*\)$/, "").trim();
  const [h, m] = cleaned.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return d;
}

export function formatTimeShort(date) {
  if (!date) return "";
  return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

// Returns today's prayer times as { name, time: Date, prayed: bool }.
// Includes Sunrise (Shuruq) between Fajr and Dhuhr as a non-prayer marker so
// users see the Fajr cutoff on the lock-screen card. `prayed` is forced to
// false for Sunrise — it's not a salah, just informational.
export function computeAllPrayers(timings, salahLog) {
  if (!timings) return [];
  const today = new Date();
  const dateStr = today.getFullYear() + "-" + String(today.getMonth() + 1).padStart(2, "0") + "-" + String(today.getDate()).padStart(2, "0");
  const todayLog = (salahLog && salahLog[dateStr]) || {};
  return ["Fajr", "Sunrise", "Dhuhr", "Asr", "Maghrib", "Isha"].map(name => ({
    name,
    time: todayAt(timings[name]) || new Date(0),
    prayed: name === "Sunrise" ? false : !!todayLog[name]
  }));
}

// Returns the next prayer relative to now from an Aladhan timings object.
// Falls back to tomorrow's Fajr if all of today's prayers have passed.
export function computeNextPrayer(timings) {
  if (!timings) return null;
  const order = [
    { name: "Fajr",    key: "Fajr" },
    { name: "Dhuhr",   key: "Dhuhr" },
    { name: "Asr",     key: "Asr" },
    { name: "Maghrib", key: "Maghrib" },
    { name: "Isha",    key: "Isha" }
  ];
  const now = new Date();
  for (const p of order) {
    const t = todayAt(timings[p.key]);
    if (t && t > now) return { name: p.name, time: t };
  }
  const tomorrowFajr = todayAt(timings.Fajr);
  if (tomorrowFajr) {
    tomorrowFajr.setDate(tomorrowFajr.getDate() + 1);
    return { name: "Fajr", time: tomorrowFajr };
  }
  return null;
}

// Jumu'ah is treated as Dhuhr on Friday — most masjids pray Jumu'ah right after Dhuhr.
export function computeJumuahTime(timings) {
  if (!timings) return null;
  const day = new Date().getDay();
  if (day !== 5) return null;
  const dhuhr = todayAt(timings.Dhuhr);
  if (!dhuhr) return null;
  if (dhuhr <= new Date()) return null;
  return dhuhr;
}

// Last third of the night = Maghrib + 2/3 of (Fajr_tomorrow - Maghrib)
export function computeTahajjudWindow(timings) {
  if (!timings) return null;
  const maghrib = todayAt(timings.Maghrib);
  const fajrToday = todayAt(timings.Fajr);
  if (!maghrib || !fajrToday) return null;
  const fajrTomorrow = new Date(fajrToday.getTime());
  fajrTomorrow.setDate(fajrTomorrow.getDate() + 1);
  const nightDurationMs = fajrTomorrow.getTime() - maghrib.getTime();
  const lastThirdStart = new Date(maghrib.getTime() + (nightDurationMs * 2 / 3));
  return { start: lastThirdStart, end: fajrTomorrow };
}
